import fetchApi from '@/lib/tools/api'
import PersonIcon from '@mui/icons-material/Person'
import {Box, Chip, Stack, Typography} from '@mui/material'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {DemoContainer} from '@mui/x-date-pickers/internals/demo'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip} from 'chart.js'
import dayjs, {Dayjs} from 'dayjs'
import 'dayjs/locale/en-gb'
import React, {useEffect, useState} from 'react'
import {useCreatePath, useTranslate} from 'react-admin'
import {Line} from 'react-chartjs-2'
import {Link} from 'react-router-dom'
import CardWithIcon from './CardWithIcon'
import PeriodSelector from './PeriodSelector'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
)

interface UserStats {
	data: {
		period: string
		producteurs: number
		clients: number
		total: number
	}[]
	period_months?: number
	period_type: 'days' | 'months'
	period_value: number
}

interface UserStatsFromDate {
	total: number
	total_clients: number
	total_producers: number
}

const UsersChartJS = () => {
	const translate = useTranslate()
	const [chartStats, setChartStats] = useState<UserStats | null>(null)
	const [dateStats, setDateStats] = useState<UserStatsFromDate | null>(null)
	const [datetime, setDatetime] = useState<dayjs.Dayjs | null>(dayjs().subtract(30, 'day'))
	const [loading, setLoading] = useState(true)
	const [totals, setTotals] = useState<{ users: number } | null>(null)
	const [selectedPeriod, setSelectedPeriod] = useState<'30d' | '90d' | '12m' | '24m' | 'custom'>('30d')
	const [startDate, setStartDate] = useState<Dayjs>()
	const [endDate, setEndDate] = useState<Dayjs>()

	// Fetch chart data with period selection
	const fetchChartStats = async (period: '30d' | '90d' | '12m' | '24m' | 'custom', customStartDate?: Dayjs, customEndDate?: Dayjs) => {
		try {
			let url = '/stats/users'

			if (period === 'custom' && customStartDate && customEndDate) {
				url += `?start_date=${customStartDate.format('YYYY-MM-DD')}&end_date=${customEndDate.format('YYYY-MM-DD')}`
			} else {
				const days = period === '30d' ? 30 : period === '90d' ? 90 : period === '12m' ? 365 : 730 // 24m = 730 days
				url += `?days=${days}`
			}

			const result = await fetchApi<UserStats>(url)
			console.log('📊 UsersChartJS: Chart data received:', result)
			setChartStats(result)
		} catch (err) {
			console.error('Error fetching users chart stats:', err)
		}
	}

	useEffect(() => {
		fetchChartStats(selectedPeriod, startDate, endDate)
	}, [])

	// Fetch totals
	useEffect(() => {
		const fetchTotals = async () => {
			try {
				const result = await fetchApi<any>('/stats/totals')
				setTotals(result)
			} catch (err) {
				console.error('Error fetching totals:', err)
			} finally {
				setLoading(false)
			}
		}

		fetchTotals()
	}, [])

	// Fetch stats from specific date (existing functionality)
	useEffect(() => {
		const fetchDateStats = async () => {
			try {
				const result = await fetchApi<UserStatsFromDate>(
					'/users/clients/stats',
					undefined,
					{
						stats: 'true',
						createdAt: dayjs(datetime).format('YYYY/MM/DD')
					},
					'GET'
				)
				setDateStats({
					total: result.total_clients + result.total_producers,
					total_clients: result.total_clients,
					total_producers: result.total_producers
				})
			} catch (err) {
				console.error('Error fetching date stats:', err)
			}
		}

		fetchDateStats()
	}, [datetime])

	const handleDateChange = (date: dayjs.Dayjs | null) => {
		setDatetime(date)
	}

	const handlePeriodChange = (period: '30d' | '90d' | '12m' | '24m' | 'custom', customStartDate?: Dayjs, customEndDate?: Dayjs) => {
		setSelectedPeriod(period)
		setStartDate(customStartDate)
		setEndDate(customEndDate)
		fetchChartStats(period, customStartDate, customEndDate)
	}

	const style = {
		box: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
			'&> a': {
				width: '-webkit-fill-available;'
			}
		},
		card: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			justifyContent: 'space-between',
			width: '-webkit-fill-available',
			padding: '0'
		},
		chipValidated: {
			backgroundColor: '#2e7d32',
			margin: 0,
			color: 'white'
		},
		chipPending: {
			backgroundColor: '#ed6c02',
			margin: 0,
			color: 'white'
		},
		divider: {
			borderColor: '#000'
		}
	}

	const [stats, setStats] = useState<{
		producers: number,
		producers_validated: number,
		producers_pending: number
	} | null>(null)
	const [error, setError] = useState<string | null>(null)

	const createPath = useCreatePath()

	useEffect(() => {
		const fetchStats = async () => {
			try {
				setLoading(true)
				const result = await fetchApi<any>('/stats/totals')
				setStats({
					producers: result.producers || 0,
					producers_validated: result.producers_validated || 0,
					producers_pending: result.producers_pending || 0
				})
				setError(null)
			} catch (err) {
				console.error('❌ ProducerStats: Error fetching stats:', err)
				setError('Erreur lors du chargement')
			} finally {
				setLoading(false)
			}
		}

		fetchStats()
	}, [])

	if (loading) {
		return (
			<CardWithIcon
				icon={PersonIcon}
				title={'Utilisateurs'}
				subtitle="Chargement..."
			>
				<Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px'}}>
					<Typography variant="body2">Chargement...</Typography>
				</Box>
			</CardWithIcon>
		)
	}

	const chartData = {
		labels: chartStats?.data.map(d => d.period) || [],
		datasets: [
			{
				label: 'Producteurs',
				data: chartStats?.data.map(d => d.producteurs) || [],
				borderColor: '#10b981',
				backgroundColor: (context: any) => {
					const ctx = context.chart.ctx
					const gradient = ctx.createLinearGradient(0, 0, 0, 80)
					gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)')
					gradient.addColorStop(1, 'rgba(16, 185, 129, 0.02)')
					return gradient
				},
				borderWidth: 2,
				tension: 0.4,
				pointRadius: 2,
				pointHoverRadius: 4,
				pointBackgroundColor: '#10b981',
				pointBorderColor: '#ffffff',
				pointBorderWidth: 1.5,
				fill: true
			},
			{
				label: 'Clients',
				data: chartStats?.data.map(d => d.clients) || [],
				borderColor: '#3b82f6',
				backgroundColor: (context: any) => {
					const ctx = context.chart.ctx
					const gradient = ctx.createLinearGradient(0, 0, 0, 80)
					gradient.addColorStop(0, 'rgba(59, 130, 246, 0.2)')
					gradient.addColorStop(1, 'rgba(59, 130, 246, 0.02)')
					return gradient
				},
				borderWidth: 2,
				tension: 0.4,
				pointRadius: 2,
				pointHoverRadius: 4,
				pointBackgroundColor: '#3b82f6',
				pointBorderColor: '#ffffff',
				pointBorderWidth: 1.5,
				fill: true
			}
		]
	}

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: {
			intersect: false,
			mode: 'index' as const
		},
		plugins: {
			legend: {
				display: false // On cache la légende pour gagner de la place
			},
			tooltip: {
				backgroundColor: 'rgba(255, 255, 255, 0.95)',
				titleColor: '#1e293b',
				bodyColor: '#475569',
				borderColor: '#e2e8f0',
				borderWidth: 1,
				cornerRadius: 6,
				padding: 8,
				boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)'
			}
		},
		scales: {
			x: {
				grid: {
					display: false
				},
				border: {
					display: false
				},
				ticks: {
					font: {
						size: 8,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
					},
					color: '#94a3b8',
					maxRotation: 0,
					maxTicksLimit: 6,
					padding: 4
				}
			},
			y: {
				grid: {
					color: 'rgba(148, 163, 184, 0.08)',
					drawBorder: false
				},
				border: {
					display: false
				},
				ticks: {
					font: {
						size: 8,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
					},
					color: '#94a3b8',
					padding: 4
				},
				beginAtZero: true
			}
		},
		elements: {
			line: {
				borderCapStyle: 'round' as const,
				borderJoinStyle: 'round' as const
			}
		}
	}

	return (
		<CardWithIcon
			icon={PersonIcon}
			title={'Utilisateurs'}
			subtitle={totals?.users || 0}
		>
			<Box sx={{display: 'flex', flexDirection: 'column'}} p={2}>
				<Box sx={{marginBottom: '10px'}}>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
						<DemoContainer components={['DatePicker']}>
							<DatePicker
								label="A partir du :"
								value={datetime}
								onChange={handleDateChange}
								slotProps={{textField: {size: 'small'}}}
							/>
						</DemoContainer>
					</LocalizationProvider>
				</Box>
				<Stack direction="row" spacing={2}>
					<Box sx={style.card}>
						<Typography variant="overline" display="block">
							Nouveaux utilisateurs
						</Typography>
						<Chip label={dateStats?.total || 0} sx={style.chipValidated} size={'small'}/>
					</Box>
					<Box sx={style.card}>
						<Link to="/producers/users">
							<Typography variant="overline" display="block">
								Producteurs
							</Typography>
							<Chip label={dateStats?.total_producers || 0} sx={style.chipValidated} size={'small'}/>
						</Link>
					</Box>
					<Box sx={style.card}>
						<Link to="/users/clients">
							<Typography variant="overline" display="block">
								Particulier
							</Typography>
							<Chip label={dateStats?.total_clients || 0} sx={style.chipPending} size={'small'}/>
						</Link>
					</Box>
				</Stack>
				{/* Graphique avec sélecteur de période */}
				<Box sx={{marginBlock: '10px'}}>
					<Box sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '2px'
					}}>
						<Typography variant="caption" sx={{fontSize: '9px'}}>
							Inscriptions {chartStats?.period_value} {chartStats?.period_type === 'days' ? 'j' : 'm'}
						</Typography>
						<PeriodSelector
							selectedPeriod={selectedPeriod}
							startDate={startDate}
							endDate={endDate}
							onPeriodChange={handlePeriodChange}
						/>
					</Box>
					<div style={{height: '120px', width: '100%'}}>
						<Line data={chartData} options={options}/>
					</div>
				</Box>

				<Box sx={{
					display: 'flex',
					justifyContent: 'space-around',
					alignItems: 'center',
					height: '75px',
					backgroundColor: 'white',
					borderRadius: '12px',
					marginBottom: '12px',
					boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
					border: '1px solid #e2e8f0'
				}}>
					<Box display={'flex'} flexDirection={'column'} sx={{textAlign: 'center'}}>
						<Typography variant="h4"
									sx={{color: '#2e7d32', fontWeight: 'bold', marginBottom: '4px', lineHeight: '1'}}>
							{stats?.producers_validated || 0}
						</Typography>
						<Typography variant="caption" sx={{color: '#64748b', fontSize: '10px'}}>
							Producteur Validés
						</Typography>
					</Box>
					<Box sx={{
						width: '1px',
						height: '40px',
						backgroundColor: '#e2e8f0'
					}}/>
					<Box display={'flex'} flexDirection={'column'} sx={{textAlign: 'center'}}>
						<Typography variant="h4"
									sx={{color: '#ed6c02', fontWeight: 'bold', marginBottom: '4px', lineHeight: '1'}}>
							{stats?.producers_pending || 0}
						</Typography>
						<Typography variant="caption" sx={{color: '#64748b', fontSize: '10px'}}>
							Producteur En attente
						</Typography>
					</Box>
				</Box>
			</Box>
		</CardWithIcon>
	)
}

export default UsersChartJS
